import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import { getAudienceToken, getLiveId } from '../../global/blocs/roomInfo'
import { fetchLiveData } from '../../global/blocs/useLiveData'
import { showNotice } from '../../Layout/Notices/Notices'
import {
  answerPoll,
  endPoll,
  getPolls,
  startPoll,
  togglePollResults,
} from '../api/polls'
import queryClient from '../../queryClient'
import LocalStorage from '../../sharedLegacy/utils/localstorage'
import { mapDtoToPoll, type Poll } from '../model/Poll'

const answeredPollsStorageKey = 'webinar-answered-polls'

const getActivePoll = (polls: Poll[] | undefined) =>
  polls?.find((poll) => poll.open || poll.displayResults)

const resolveObjectToken = async () =>
  getAudienceToken() ?? (await fetchLiveData())?.token

type PollsQueryOptions = {
  /** Restrict the response to polls marked public */
  publicOnly?: boolean
  /**
   * Request on the producer's own session auth rather than an object token.
   * Object-token requests omit per-option answer counts.
   */
  asProducer?: boolean
}

const pollsQueryKey = ({
  publicOnly,
  asProducer,
}: Required<PollsQueryOptions>) => ['polls', { publicOnly, asProducer }]

export const usePollsQuery = ({
  publicOnly = false,
  asProducer = false,
}: PollsQueryOptions = {}) =>
  useQuery(
    pollsQueryKey({ publicOnly, asProducer }),
    async (): Promise<Poll[]> => {
      const polls = await getPolls(getLiveId(), {
        objectToken: asProducer ? undefined : await resolveObjectToken(),
        publicOnly,
      })

      return polls.map(mapDtoToPoll)
    },
    { staleTime: asProducer ? 0 : 30_000 }
  )

export const invalidatePolls = () => queryClient.invalidateQueries(['polls'])

const readStoredAnsweredPollIds = (): number[] => {
  try {
    const stored = LocalStorage.get(answeredPollsStorageKey)
    const parsed: unknown = stored ? JSON.parse(stored) : []

    return Array.isArray(parsed)
      ? parsed.map(Number).filter(Number.isFinite)
      : []
  } catch {
    return []
  }
}

export const usePollsList = ({
  publicOnly = false,
  isVotingAllowed = false,
} = {}) => {
  const { data } = usePollsQuery({ publicOnly })
  const [snapshot] = useState(() =>
    isVotingAllowed ? readStoredAnsweredPollIds() : []
  )

  const polls = useMemo(() => {
    if (!data || snapshot.length === 0) {
      return data ?? []
    }

    return data.filter((poll) => !snapshot.includes(poll.pollId) || !poll.open)
  }, [data, snapshot])

  return { polls, activePoll: getActivePoll(polls) }
}

const useSessionAnsweredPolls = () => {
  const [answeredPollIds, setAnsweredPollIds] = useState(
    () => new Set<number>()
  )

  const markAnswered = useCallback((pollId: number) => {
    setAnsweredPollIds((current) => new Set(current).add(pollId))
    LocalStorage.set(
      answeredPollsStorageKey,
      JSON.stringify([...new Set([...readStoredAnsweredPollIds(), pollId])])
    )
  }, [])

  return { answeredPollIds, markAnswered }
}

const useActivatePollMutation = () =>
  useMutation({
    mutationKey: ['activate-poll'],
    mutationFn: async (poll: Poll): Promise<void> => {
      switch (poll.state) {
        case 'start':
          await startPoll(poll.pollId)
          return
        case 'finish':
          await endPoll(poll.pollId)
          return
        case 'publish':
          await togglePollResults(poll.pollId, true)
          return
        case 'unpublish':
          await togglePollResults(poll.pollId, false)
      }
    },
    onError: (error: Error) => {
      showNotice({
        message: error.message,
        expirationMillis: 5e3,
        isClosable: true,
      })
    },
    onSettled: () => invalidatePolls(),
  })

const useAnswerPollMutation = (onAnswered: (pollId: number) => void) =>
  useMutation({
    mutationKey: ['answer-poll'],
    mutationFn: async ({
      poll,
      optionId,
      uuid,
    }: {
      poll: Poll
      optionId: number
      uuid?: string
    }) => {
      await answerPoll({
        objectId: getLiveId(),
        objectToken: await resolveObjectToken(),
        pollId: poll.pollId,
        uuid,
        optionId,
      })

      return poll.pollId
    },
    onSuccess: onAnswered,
  })

export const usePolls = ({
  publicOnly = false,
  isVotingAllowed = false,
} = {}) => {
  const { polls } = usePollsList({
    publicOnly,
    isVotingAllowed,
  })
  const { answeredPollIds, markAnswered } = useSessionAnsweredPolls()

  const { mutate: activatePoll } = useActivatePollMutation()
  const { mutate: mutateAnswerPoll } = useAnswerPollMutation(markAnswered)

  const answerPollHandler = useCallback(
    (poll: Poll, optionId: number, uuid?: string) =>
      mutateAnswerPoll({ poll, optionId, uuid }),
    [mutateAnswerPoll]
  )

  return {
    polls,
    answeredPollIds,
    activatePoll,
    answerPoll: answerPollHandler,
  }
}
